import * as React from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import type { CustomerList, CustomerContactList } from './models/CustomerInterfaces';
import type { JobDetailsNew, NewJobQuote, NewJobQuoteItem, JobQuoteExtraItemOption, NewJobQuoteInvoiceExtra, FenceAreaOption, AddressDetails, SiteContacts, JobAddress } from './models/JobInterfaces';
import type { Branch, GenericList, ItemOptionList } from './models/JobSharedInterfaces';
import { CheckPermissions, GetQuery, PostFormDataQuery, PostQuery } from './Shared';
import * as UtilsGoogleMaps from './UtilsGoogleMaps';
import { Stimulsoft } from 'stimulsoft-reports-js/Scripts/stimulsoft.viewer';
import NewJobDetails from './NewJobDetails';
import NewJobMatchingJobs from './NewJobMatchingJobs';
import NewJobPricelist from './NewJobPricelist';
import NewJobInvoice from './NewJobInvoice';
import NewJobNote from './NewJobNotes';

const NewJobData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const { typeId, copyId } = useParams();
    const [systemCompanyId, setSystemCompanyId] = React.useState(0);
    const [chargeFuelSurchargeSwitchedOn, setChargeFuelSurchargeSwitchedOn] = React.useState(true);
    const [currentStep, setCurrentStep] = React.useState(1);
    const jobDetailsInit: JobDetailsNew = {
        id: 0,
        copyJobId: 0,
        customerName: "",
        customerId: -1,
        customerExists: true,
        customerContactFirstName: "",
        customerContactLastName: "",
        customerContactPhone: "",
        customerContactEmail: "",
        customerAddress: "",
        customerStreetNumber: "",
        customerStreetName: "",
        customerSuburb: "",
        customerState: "",
        customerCountry: "",
        customerPostCode: "",
        customerGooglePlaceId: "",
        customerAddressLatitude: 0,
        customerAddressLongitude: 0,
        customerAbn: "",
        customerContactId: -1,
        customerCashSaleAccount: false,
        customerInvoiceQuotedAmount: false,
        customerChargeFuelSurcharge: true,
        customerNewFuelInvoice: false,
        canSendEmailToCustomer: true,
        useTyabbPricelist: false,
        sendEmailToCustomer: true,
        accountTypeId: -1,
        accountType: "",
        accountLimitFormatted: "",
        customerAllowOnGoingHire: true,
        customerQuoteNotesDunny: "",
        siteAddressExists: true,
        siteAddress: "",
        siteAddressDistance: 0,
        siteAddressDistanceFormatted: "",
        siteAddressDuration: 0,
        siteAddressDurationFormatted: "",
        addressNearSiteAddress: "",
        unitNumber: "",
        streetNumber: "",
        streetName: "",
        suburb: "",
        state: "",
        country: "",
        postCode: "",
        manualStreetName: "",
        manualSuburb: "",
        manualState: "VIC",
        manualCountry: "Australia",
        manualPostCode: "",
        lotNumber: "",
        isEvent: false,
        isThreeDayHire: false,
        eventName: "",
        eventStartDate: "",
        eventEndDate: "",
        deliveryWindowStart: "",
        deliveryWindowEnd: "",
        deliveryPlacementNotes: "",
        gateCode: "",
        jobType: 1,
        paymentType: -1,
        truckAccess: -1,
        parking: -1,
        siteClean: -1,
        powerlinesWithinRange: -1,
        weekdayAccess: -1,
        weekendAccess: -1,
        phoneCallRequired: -1,
        notes: "",
        copyQuote: true,
        linkJob: false,
        googlePlaceId: "",
        siteAddressLatitude: 0,
        siteAddressLongitude: 0,
        addressGooglePlaceId: "",
        addressSiteAddressLatitude: 0,
        addressSiteAddressLongitude: 0,
        addressSiteAddressDistance: 0,
        addressSiteAddressDistanceFormatted: "",
        addressSiteAddressDuration: 0,
        addressSiteAddressDurationFormatted: "",
        closestBranchId: 1,
        linkedToReckon: true,
        locked: false,
        reference: "",
        priceOnly: false
    };
    const [jobDetails, setJobDetails] = React.useState<JobDetailsNew>(jobDetailsInit);
    const initQuoteItem: NewJobQuoteItem = {
        itemIndex: 0,
        itemId: -1,
        item: "",
        itemTypeId: 0,
        productGroupId: 0,
        itemDescription: "",
        installOnly: false,
        extendable: true,
        isEws: false,
        isWaste: false,
        isUnderground: false,
        isEvent: false,
        unitNumber: "",
        scheduleDate: "",
        onHireDate: "",
        offhireDate: "",
        pickupDate: "",
        hirePeriod: 0,
        defaultHirePeriod: 0,
        customHirePeriod: false,
        frequencyId: -1,
        locationId: -1,
        fenceBraceId: -1,
        hours: 0,
        size: 0,
        quantity: 1,
        unitOfMeasure: "",
        initialHire: 0,
        travel: 0,
        cleaning: 0,
        trench: 0,
        confinedSpace: 0,
        fuelSurcharge: 0,
        amount: 0,
        overhireRate: "0.00",
        truck: 0,
        truckDoors: -1,
        purchaseOrderNumber: "",
        purchaseOrderNumberFuel: "",
        notes: "",
        trolley: false,
        extras: {
            itemId: -1,
            quantity: 0,
            equipped: false
        },
        fencingStock: {
            panels: 0,
            blocks: 0,
            clamps: 0,
            pickets: 0,
            signs: 0,
            braces: 0
        },
        calculatedPriceNote: "",
        deliveryAddress: "",
        deliveryAddressGooglePlaceId: "",
        deliveryAddressLatitude: 0,
        deliveryAddressLongitude: 0,
        invoiceDescription: "",
        incStepsTravel: false,
        invoicePeriodTypeId: 1
    };
    const initJobQuote: NewJobQuote = {
        siteContactName: "",
        siteContactPhone: "",
        siteContact2: "",
        customerPickingUp: -1,
        jobInstalled: false,
        jobInvoiced: false,
        jobInvoicedDate: "",
        schedulePickup: false,
        paymentType: -1,
        items: [ initQuoteItem ],
        invoiceItems: [],
        tiltSlideClearance: -1,
        cameraLocation: "",
        powerPoleQuestions: {
            isUnderground: false,
            superSafeConnecting: -1,
            superSafeSupplierId: -1,
            powerPoleLocationId: -1,
            powerPreviouslyConnected: -1,
            powerAbolished: -1,
            siteLocked: -1,
            clearSightLine: -1,
            pitOnSite: -1,
            pitDistanceId: -1,
            superSafeDiggingTrench: -1
        },
        fenceQuestions: {
            fenceTotalMetres: 0,
            treeProtection: false,
            highWindArea: false,
            shadeClothAdded: false,
            signageAdded: false,
            signagePanels: 0,
            fenceAreas: []
        },
        epaQuestions: {
            tankLidExposed: -1,
            tankLidLocationKnown: -1,
            tankLidLocation: "",
            truckLargeAccess: -1,
            truckSmallAccess: -1,
            truckAccessToTank: -1,  //within 25m
            steepDriveway: -1,
            hoseMetres: 0,
            sealedDriveway: -1,
            lastService: "",
            waterBoard: "",
            waterAccessOnSite: -1,
            emergencySameDayJob: -1,
            weekendJob: -1,
            siteVisit: -1,
            confinedSpace: -1,
            materialSafetyDataSheet: -1,
            sampleTestingRequired: -1
        },
        total: 0,
        totalFormatted: "$0.00",
        invoiceTotal: 0,
    };
    const [jobQuote, setJobQuote] = React.useState<NewJobQuote>(initJobQuote);
    const [jobFiles, setJobFiles] = React.useState<File[]>([]);
    const [defaultFenceAreas, setDefaultFenceAreas] = React.useState<FenceAreaOption[]>([]);
    const [branches, setBranches] = React.useState<Branch[]>([]);
    const [customers, setCustomers] = React.useState<CustomerList[]>([]);
    const [customerContacts, setCustomerContacts] = React.useState<CustomerContactList[]>([]);
    const [updateCustContacts, setUpdateCustContacts] = React.useState(false);  //set when we need to get a new list of customer contacts
    const [yesNo, setYesNo] = React.useState<GenericList[]>([]);
    const [itemOptions, setItemOptions] = React.useState<ItemOptionList[]>([]);
    const [extraItemOptions, setExtraItemOptions] = React.useState<JobQuoteExtraItemOption[]>([]);
    const [frequencyOptions, setFrequencyOptions] = React.useState<GenericList[]>([]);
    const [truckOptions, setTruckOptions] = React.useState<GenericList[]>([]);
    const [truckSideOptions, setTruckSideOptions] = React.useState<GenericList[]>([]);
    const [powerPoleDistanceOptions, setPowerPoleDistanceOptions] = React.useState<GenericList[]>([]);
    const [powerPoleLocationOptions, setPowerPoleLocationOptions] = React.useState<GenericList[]>([]);
    const [superSafeSupplierOptions, setSuperSafeSupplierOptions] = React.useState<GenericList[]>([]);
    const [epaExtra30MinOnSiteFeeFormatted, setEpaExtra30MinOnSiteFeeFormatted] = React.useState("");
    const [weekendFeeFormatted, setWeekendFeeFormatted] = React.useState("");
    const [emergencyFeeFormatted, setEmergencyFeeFormatted] = React.useState("");
    const [truckHireRate, setTruckHireRate] = React.useState(0);
    const [allowAllocateToPastDate, setAllowAllocateToPastDate] = React.useState(false);
    const [lunchRoomExtraId, setLunchRoomExtraId] = React.useState(0);
    const [siteOfficeExtraId, setSiteOfficeExtraId] = React.useState(0);
    const [resetQuote, setResetQuote] = React.useState(false);
    const [goingNext, setGoingNext] = React.useState(false); //used for matching jobs
    const [saveDisabled, setSaveDisabled] = React.useState(false);
  
    React.useEffect(() => {
        getData();
    }, [typeId])

    const getData = async () => {
        var hasPermission = await CheckPermissions("CreateJob");
        if (hasPermission) {
            getJobData();
        } else {
            navigate("/accessdenied");
        }
    }

    const getJobData = async () => {
        try {
            const apiUrl = "GetDropdowns";
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {

                var systemCompanyId = result.data.systemCompanyId;
                setJobDetails(prevState => ({ ...prevState, jobType: Number(typeId) }));
                if (result.data.isTyabbUser) {
                    jobDetails.useTyabbPricelist = true;
                }
                let fenceAreas = result.data.fenceAreas;
                setDefaultFenceAreas(fenceAreas);
                setResetQuote(true);
                setJobFiles([]);
                setBranches(result.data.branchOptions);
                setSystemCompanyId(systemCompanyId);
                setChargeFuelSurchargeSwitchedOn(result.data.chargeFuelSurchargeSwitchedOn);
                setAllowAllocateToPastDate(result.data.allowAllocateToPastDate);
                setTruckOptions(result.data.truckOptions);
                setCustomers(result.data.customers);
                setYesNo(result.data.yesNo);
                setItemOptions(result.data.itemOptions);
                setExtraItemOptions(result.data.extraItemOptions);
                setFrequencyOptions(result.data.frequencyOptions);
                setTruckSideOptions(result.data.truckSideOptions);
                setPowerPoleDistanceOptions(result.data.powerPoleDistanceOptions);
                setPowerPoleLocationOptions(result.data.powerPoleLocationOptions);
                setSuperSafeSupplierOptions(result.data.superSafeSupplierOptions);
                setLunchRoomExtraId(result.data.lunchRoomExtraId);
                setSiteOfficeExtraId(result.data.siteOfficeExtraId);
                setEpaExtra30MinOnSiteFeeFormatted(result.data.epaExtra30MinOnSiteFeeFormatted);
                setTruckHireRate(result.data.truckHireRate);
                setEmergencyFeeFormatted(result.data.emergencyFeeFormatted);
                setWeekendFeeFormatted(result.data.weekendFeeFormatted);
                setGoingNext(false);
                if (Number(copyId) > 0) {
                    getCopyJobDetails(systemCompanyId);
                } else {
                    setLoading(false);
                }
            }
        } catch (e) {
            toast.error("Error getting data!");
        }
    }

    const getCopyJobDetails = async (companyId: number) => {
        try {
            const apiUrl = 'GetCopyJobDetails?CopyJobId=' + Number(copyId);
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var job = result.data.jobDetails;
                job.id = 0;  //set the id back to 0 as it is a new job
                job.copyQuote = true;
                job.linkJob = companyId === 2 ? true : false;
                job.copyJobId = Number(copyId);
                job.accountTypeId = result.data.accountTypeId;
                job.customerAllowOnGoingHire = result.data.allowOnGoingHire;
                job.customerQuoteNotesDunny = result.data.quoteNotesDunny;
                job.customerExists = true;
                job.customerAddress = "";
                job.customerStreetNumber = "";
                job.customerStreetName = "";
                job.customerSuburb = "";
                job.customerState = "";
                job.customerCountry = "";
                job.customerPostCode = "";
                job.customerGooglePlaceId = "";
                job.customerAddressLatitude = "";
                job.customerAddressLongitude = "";
                job.customerAbn = "";
                job.notes = "";
                job.priceOnly = false;
                job.useTyabbPricelist = false;
                job.canSendEmailToCustomer = result.data.canEmailCustomer;

                setJobDetails(job);
                var quote = result.data.jobQuote;
                //add blank row
                var quoteItems = quote.items;
                var quoteItem = addBlankQuoteItem(quoteItems.length);
                quoteItems.push(quoteItem);
                setJobQuote(quote);
                setCustomerContacts(result.data.customerContacts);
                setLoading(false);
            }
        } catch (e) {
            toast.error("Error getting data!");
        }
    }

    React.useEffect(() => {
        if (resetQuote) {
            resetJobQuote();
        }
    }, [resetQuote])

    const resetJobQuote = () => {
        //add blank row
        var quoteItem = addBlankQuoteItem(0);
        var quoteItems: NewJobQuoteItem[] = [];
        quoteItems.push(quoteItem);

        var resetJobQuote: NewJobQuote = {
            siteContactName: "",
            siteContactPhone: "",
            siteContact2: "",
            customerPickingUp: -1,
            jobInstalled: false,
            jobInvoiced: false,
            jobInvoicedDate: "",
            schedulePickup: false,
            paymentType: -1,
            items: quoteItems,
            invoiceItems: [],
            tiltSlideClearance: -1,
            cameraLocation: "",
            powerPoleQuestions: {
                isUnderground: false,
                superSafeConnecting: -1,
                superSafeSupplierId: -1,
                powerPoleLocationId: -1,
                powerPreviouslyConnected: -1,
                powerAbolished: -1,
                siteLocked: -1,
                clearSightLine: -1,
                pitOnSite: -1,
                pitDistanceId: -1,
                superSafeDiggingTrench: -1
            },
            fenceQuestions: {
                fenceTotalMetres: 0,
                treeProtection: false,
                highWindArea: false,
                shadeClothAdded: false,
                signageAdded: false,
                signagePanels: 0,
                fenceAreas: defaultFenceAreas
            },
            epaQuestions: {
                tankLidExposed: -1,
                tankLidLocationKnown: -1,
                tankLidLocation: "",
                truckLargeAccess: -1,
                truckSmallAccess: -1,
                truckAccessToTank: -1,  //within 25m
                steepDriveway: -1,
                hoseMetres: 0,
                sealedDriveway: -1,
                lastService: "",
                waterBoard: "",
                waterAccessOnSite: -1,
                emergencySameDayJob: -1,
                weekendJob: -1,
                siteVisit: -1,
                confinedSpace: -1,
                materialSafetyDataSheet: -1,
                sampleTestingRequired: -1
            },
            total: 0,
            totalFormatted: "$0.00",
            invoiceTotal: 0
        };

        setJobQuote(resetJobQuote);
    }

    React.useEffect(() => {
        if (updateCustContacts && jobDetails.customerId && jobDetails.customerId > 0) {
            getCustomerContacts(jobDetails.customerId);
        }
    }, [updateCustContacts]);

    const updateJobDetails = (fieldName: string, fieldValue: string, resetItems: boolean) => {
        setJobDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));

        //if (fieldName === "manualStreetName" || fieldName === "manualSuburb" || fieldName === "manualState" || fieldName === "manualPostCode" || fieldName === "siteAddressExists" || fieldName === "siteAddress") {
        //    //save the data to state - so we can pass to other items
        //    setJobAddress(prevState => ({
        //        ...prevState,
        //        manualStreetName: jobDetails.manualStreetName,
        //        manualSuburb: jobDetails.manualSuburb,
        //        manualState: jobDetails.manualState,
        //        manualPostCode: jobDetails.manualPostCode,
        //        siteAddressExists: jobDetails.siteAddressExists,
        //        siteAddress: jobDetails.siteAddress
        //    }));
        //}

        if (resetItems) {
            var quoteItems: NewJobQuoteItem[] = [];
            var item = addBlankQuoteItem(0);
            quoteItems.push(item);
            setJobQuote(prevState => ({ ...prevState, items: quoteItems }));
        }
    }

    const addBlankQuoteItem = (itemIndex: number) => {
        var quoteItem: NewJobQuoteItem = {
            itemIndex: itemIndex,
            itemId: -1,
            item: "",
            itemTypeId: 0,
            productGroupId: 0,
            itemDescription: "",
            installOnly: false,
            extendable: true,
            isEws: false,
            isWaste: false,
            isUnderground: false,
            isEvent: false,
            unitNumber: "",
            scheduleDate: "",
            onHireDate: "",
            offhireDate: "",
            pickupDate: "",
            hirePeriod: 0,
            defaultHirePeriod: 0,
            customHirePeriod: false,
            frequencyId: -1,
            locationId: -1,
            fenceBraceId: -1,
            hours: 0,
            size: 0,
            quantity: 1,
            unitOfMeasure: "",
            initialHire: 0,
            travel: 0,
            cleaning: 0,
            trench: 0,
            confinedSpace: 0,
            fuelSurcharge: 0,
            amount: 0,
            overhireRate: "0.00",
            truck: 0,
            truckDoors: -1,
            purchaseOrderNumber: "",
            purchaseOrderNumberFuel: "",
            notes: "",
            trolley: false,
            extras: {
                itemId: -1,
                quantity: 0,
                equipped: false
            },
            fencingStock: {
                panels: 0,
                blocks: 0,
                clamps: 0,
                pickets: 0,
                signs: 0,
                braces: 0
            },
            calculatedPriceNote: "",
            deliveryAddress: "",
            deliveryAddressGooglePlaceId: "",
            deliveryAddressLatitude: 0,
            deliveryAddressLongitude: 0,
            invoiceDescription: "",
            incStepsTravel: false,
            invoicePeriodTypeId: 1
        };
        return quoteItem;
    }

    const updateJobDetailsNumber = (fieldName: string, fieldValue: number, resetItems: boolean) => {
        setJobDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));

        if (resetItems) {
            var quoteItems: NewJobQuoteItem[] = [];
            var item = addBlankQuoteItem(0);
            quoteItems.push(item);
            setJobQuote(prevState => ({ ...prevState, items: quoteItems }));
        }
    }

    const updateJobDetailsChecked = (fieldName: string, fieldValue: boolean, resetItems: boolean) => {
        setJobDetails(prevState => ({ ...prevState, [fieldName]: fieldValue }));

        if (fieldName === "isEvent" || fieldName === "isThreeDayHire" || (jobDetails.priceOnly && fieldName === "useTyabbPricelist")) {
            //need to reset customer exists if they untick is event
            setJobDetails(prevState => ({
                ...prevState,
                customerExists: true,
                customerId: -1,
                customerContactId: -1,
                customerName: "",
                customerContactFirstName: "",
                customerContactLastName: "",
                customerContactPhone: "",
                customerContactEmail: ""
            }));
        } else if (fieldName === "priceOnly") {
            setJobDetails(prevState => ({ ...prevState, customerExists: true }));
        }

        //don't want both selected
        if (fieldName === "isEvent") {
            setJobDetails(prevState => ({ ...prevState, isThreeDayHire: false }));
        } else if (fieldName === "isThreeDayHire") {
            setJobDetails(prevState => ({ ...prevState, isEvent: false }));
        }
  
        if (!jobDetails.isEvent && !jobDetails.isThreeDayHire) {
            //reset event fields
            setJobDetails(prevState => ({
                ...prevState,
                eventStartDate: "",
                eventEndDate: "",
                deliveryWindowStart: "",
                deliveryWindowEnd: ""
            }));
        }

        if (resetItems) {
            var quoteItems: NewJobQuoteItem[] = [];
            var item = addBlankQuoteItem(0);
            quoteItems.push(item);
            setJobQuote(prevState => ({ ...prevState, items: quoteItems }));
        }
    }

    const updateAddress = async (addressType: number, updatedAddress: AddressDetails) => {
        if (addressType === 1) {
            setAddress(updatedAddress);
        } else if (addressType === 2) {
            setNearestAddress(updatedAddress);
        } else if (addressType === 3) {
            setCustomerAddress(updatedAddress);
        }
    }

    const setAddress = async (updatedAddress: AddressDetails) => {
        var existingDuration = jobDetails.siteAddressDuration;
        var addDuration = await UtilsGoogleMaps.calculateAddressDuration(branches, updatedAddress);

        setJobDetails(prevState => ({
            ...prevState,
            siteAddress: updatedAddress.fullAddress,
            streetNumber: updatedAddress.street_number,
            streetName: updatedAddress.route,
            suburb: updatedAddress.locality,
            state: updatedAddress.administrative_area_level_1,
            country: updatedAddress.country,
            postCode: updatedAddress.postal_code,
            googlePlaceId: updatedAddress.placeId,
            siteAddressLatitude: updatedAddress.latitude,
            siteAddressLongitude: updatedAddress.longitude,
            siteAddressDistance: addDuration.distance,
            siteAddressDistanceFormatted: addDuration.distance + " km",
            siteAddressDuration: addDuration.duration,
            siteAddressDurationFormatted: addDuration.duration + "mins",
            closestBranchId: addDuration.branchId
        }));

        //only reset the quote if travel time is more than 90 mins as that re-calculates the travel amount
        var resetExistQuote = existingDuration > 90 || addDuration.duration > 90;
        setResetQuote(resetExistQuote);
    }

    const setNearestAddress = async (updatedAddress: AddressDetails) => {
        var addDuration = await UtilsGoogleMaps.calculateAddressDuration(branches, updatedAddress);

        setJobDetails(prevState => ({
            ...prevState,
            addressNearSiteAddress: updatedAddress.fullAddress,
            addressGooglePlaceId: updatedAddress.placeId,
            addressSiteAddressLatitude: updatedAddress.latitude,
            addressSiteAddressLongitude: updatedAddress.longitude,
            addressSiteAddressDistance: addDuration.distance,
            addressSiteAddressDistanceFormatted: addDuration.distance + " km",
            addressSiteAddressDuration: addDuration.duration,
            addressSiteAddressDurationFormatted: addDuration.duration + "mins",
            closestBranchId: addDuration.branchId
        }));
    }

    const setCustomerAddress = async (updatedAddress: AddressDetails) => {
        setJobDetails(prevState => ({
            ...prevState,
            customerAddress: updatedAddress.fullAddress,
            customerStreetNumber: updatedAddress.street_number,
            customerStreetName: updatedAddress.route,
            customerSuburb: updatedAddress.locality,
            customerState: updatedAddress.administrative_area_level_1,
            customerCountry: updatedAddress.country,
            customerPostCode: updatedAddress.postal_code,
            customerGooglePlaceId: updatedAddress.placeId,
            customerAddressLatitude: updatedAddress.latitude,
            customerAddressLongitude: updatedAddress.longitude,
        }));
    } 

    const clearSiteAddress = (addrExists: boolean) => {
        //clear site address details when switching
        setJobDetails(prevState => ({
            ...prevState,
            siteAddressExists: !addrExists,
            siteAddress: "",
            siteAddressDistance: 0,
            siteAddressDistanceFormatted: "",
            siteAddressDuration: 0,
            siteAddressDurationFormatted: "",
            streetNumber: "",
            streetName: "",
            suburb: "",
            state: "",
            country: "",
            postCode: "",
            manualStreetName: "",
            manualSuburb: "",
            manualState: "VIC",
            manualCountry: "Australia",
            manualPostCode: "",
        }));
    }

    const clearCustomerAddress = (custExists: boolean) => {
        setJobDetails(prevState => ({
            ...prevState,
            customerExists: !custExists,
            customerId: -1,
            customerContactId: -1,
            customer: "",
            customerContactFirstName: "",
            customerContactLastName: "",
            customerContactPhone: "",
            customerContactEmail: "",
            gateCode: "",
            accountTypeId: 1,
            accountType: "Prepaid",
            accountLimitFormatted: "",
            customerAllowOnGoingHire: systemCompanyId === 3 ? true : false,
            customerQuoteNotesDunny: "",
            linkedToReckon: false,
            customerCashSaleAccount: false,
            locked: false
        }));
    }

    const updateCustomer = async (customerId: number) => {
        setJobDetails(prevState => ({
            ...prevState,
            customerId: customerId,
            customerContactId: -1,
            useTyabbPricelist: false
        }));
 
        //set the gate code
        if (customerId >= 0) {
            var customer = customers.find(c => c.id == customerId);
            if (customer) {
                setJobDetails(prevState => ({
                    ...prevState,
                    gateCode: customer!.gateCode ?? "",
                    accountTypeId: customer!.accountTypeId,
                    accountType: customer!.accountType,
                    accountLimitFormatted: customer!.accountLimitFormatted,
                    customerAllowOnGoingHire: customer!.allowOnGoingHire,
                    customerQuoteNotesDunny: customer!.quoteNotesDunny ?? "",
                    locked: customer!.locked,
                    customerName: customer!.name,
                    customerContactId: -1,
                    customerContactFirstName: "",
                    customerContactLastName: "",
                    customerContactPhone: "",
                    customerContactEmail: "",
                    sendEmailToCustomer: customer!.canEmailCustomer,
                    linkedToReckon: customer!.accountIdDunny != "",
                    customerCashSaleAccount: customer!.cashSaleAccount,
                    customerInvoiceQuotedAmount: customer!.invoiceQuotedAmount,
                    customerChargeFuelSurcharge: customer!.chargeFuelSurcharge,
                    customerNewFuelInvoice: customer!.newFuelInvoice,
                    canSendEmailToCustomer: customer!.canEmailCustomer
                }));
                if (customer.accountTypeId === 2) {
                    setJobQuote(prevState => ({ ...prevState, paymentType: 1 }));
                } else if (customer.accountTypeId === 3) {
                    setJobQuote(prevState => ({ ...prevState, paymentType: 2 }));
                } else {
                    setJobQuote(prevState => ({ ...prevState, paymentType: -1 }));
                }
                setUpdateCustContacts(true);
            } else {
                setJobDetails(prevState => ({
                    ...prevState,
                    accountType: "",
                    accountLimitFormatted: ""
                }));
            }
        } else {
            setCustomerContacts([]);
        }
    }

    const getCustomerContacts = async (custId: number) => {
        try {
            const apiUrl = 'GetCustomerContact?customerId=' + custId + '&customerContactId=0';
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                setCustomerContacts(result.data);
                //if we are using a cash sales customer then set the customer contact to the first contact in the list - should be an SS contact person
                if (jobDetails.customerCashSaleAccount && result.data.length > 0) {
                    var firstContact = result.data[0]; //should only be one
                    if (firstContact) {
                        setJobDetails(prevState => ({ ...prevState, customerContactId: firstContact.id }));
                    }
                }
                setUpdateCustContacts(false);
            }
        } catch (e) {
            toast.error("Error getting data!");
            setUpdateCustContacts(false);
        }
    }

    const updateCustomerContacts = (updatedContacts: CustomerContactList[]) => {
        setCustomerContacts(updatedContacts);
    }

    const updateJobQuote = (jobQuote: NewJobQuote) => {
        //update total
        var total = 0;
        var invoiceTotal = 0;

        for (var i = 0; i < jobQuote.items.length; i++) {
            var amount = jobQuote.items[i].amount * 1;
            total += amount;
        }

        //include extra invoice items
        invoiceTotal += total;
        for (var i = 0; i < jobQuote.invoiceItems.length; i++) {
            invoiceTotal += jobQuote.invoiceItems[i].amount * 1;
        }

        var totalFormatted = (total).toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        setJobQuote(prevState => ({ ...prevState, total: total, totalFormatted: totalFormatted , invoiceTotal: invoiceTotal }))
    }

    const updateSiteContacts = (fieldName: string, fieldValue: string) => {
        setJobQuote(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    //type 1: add files, type 2: replace files
    const updateJobFiles = (uploadedFiles: File[], type: number) => {
        var files = [...jobFiles];

        if (type === 1) {
            for (var i = 0; i < uploadedFiles.length; i++) {
                files.push(uploadedFiles[i]);
            }
            setJobFiles(files);
        } else {
            setJobFiles(uploadedFiles);
        }
    }

    const addInvoiceItem = (invoiceItem: NewJobQuoteInvoiceExtra) => {
        let invItems = [...jobQuote.invoiceItems];
        invItems.push(invoiceItem);

        //calculate price
        var extraInvItemsPrice = calculateExtraItemsTotal(invItems);
        let totalInvPrice = (jobQuote.total * 1) + extraInvItemsPrice;

        setJobQuote(prevState => ({ ...prevState, invoiceTotal: totalInvPrice, invoiceItems: invItems }));
    }

    const deleteInvoiceItem = (deleteIndex: number) => {
        let invItems = [...jobQuote.invoiceItems];
        invItems.splice(deleteIndex, 1);

        //calculate price
        var extraInvItemsPrice = calculateExtraItemsTotal(invItems);
        let totalInvPrice = (jobQuote.total * 1) + extraInvItemsPrice;

        setJobQuote(prevState => ({ ...prevState, invoiceTotal: totalInvPrice, invoiceItems: invItems }));
    }

    const calculateExtraItemsTotal = (extraInvoiceItems: NewJobQuoteInvoiceExtra[]) => {
        var extraInvItemTotal = 0;
        for (var i = 0; i < extraInvoiceItems.length; i++) {
            extraInvItemTotal += extraInvoiceItems[i].amount * 1;
        }

        return extraInvItemTotal;
    }

    const updateJobNotes = (updatedNotes: string) => {
        setJobDetails(prevState => ({ ...prevState, notes: updatedNotes }));
    }

    const updateGateCode = (updatedGateCode: string) => {
        setJobDetails(prevState => ({ ...prevState, gateCode: updatedGateCode }));
    }

    const _next = () => {
        //if we are on the last step and user clicks finish button, then save to the database
        if (currentStep == 5) {
            if (saveDisabled) {
                return;
            }
            setSaveDisabled(true);

            if (jobDetails.priceOnly) {
                toast.error("You cannot save PRICE ONLY jobs. Remove the Price Only check from Job Details screen.");
            } else {
                saveJobDetails();
            }
        } else {
            // Make sure currentStep is set to something reasonable
            if (currentStep >= 4) {
                setCurrentStep(5);
            } else {
                var jobTypeId = (jobDetails.jobType * 1);

                if (currentStep === 2 && jobTypeId === 1) {
                    setCurrentStep(5);  //skip pricelist and confirmation step as job will be quoted later
                } else {
                    setCurrentStep(currentStep + 1);
                }
            }

            setGoingNext(true);
        }
    }

    const getJobQuoteItems = () => {
        var quoteItems = [...jobQuote.items];
        var quoteItemsToSave: NewJobQuoteItem[] = [];
        for (var i = 0; i < quoteItems.length - 1; i++) {
            var quoteItemToSave = quoteItems[i];
            if (quoteItemToSave.customHirePeriod && quoteItemToSave.invoicePeriodTypeId === 1) {
                //need to change hire period to days for saving (don't convert monthly hire periods)
                quoteItemToSave.hirePeriod = quoteItems[i].hirePeriod * 7;
            }
            //cater for blank dates, api needs null
            quoteItemToSave.scheduleDate = quoteItemToSave.scheduleDate === "" ? null : quoteItemToSave.scheduleDate;
            quoteItemToSave.onHireDate = quoteItemToSave.onHireDate === "" ? null : quoteItemToSave.onHireDate;
            quoteItemToSave.pickupDate = quoteItemToSave.pickupDate === "" ? null : quoteItemToSave.pickupDate;

            quoteItemsToSave.push(quoteItemToSave);
        }

        return quoteItemsToSave;
    }

    const saveJobDetails = async () => {
        let items = getJobQuoteItems();
        jobQuote.items = items;
        
        //clear job quote as pricelist hasn't been selected
        var clearQuote: NewJobQuote = {
            siteContactName: "",
            siteContactPhone: "",
            siteContact2: "",
            customerPickingUp: -1,
            jobInstalled: false,
            jobInvoiced: false,
            jobInvoicedDate: null,
            schedulePickup: false,
            paymentType: -1,
            items: [],
            invoiceItems: [],
            tiltSlideClearance: -1,
            cameraLocation: "",
            powerPoleQuestions: {
                isUnderground: false,
                superSafeConnecting: -1,
                superSafeSupplierId: -1,
                powerPoleLocationId: -1,
                powerPreviouslyConnected: -1,
                powerAbolished: -1,
                siteLocked: -1,
                clearSightLine: -1,
                pitOnSite: -1,
                pitDistanceId: -1,
                superSafeDiggingTrench: -1
            },
            fenceQuestions: {
                fenceTotalMetres: 0,
                treeProtection: false,
                highWindArea: false,
                shadeClothAdded: false,
                signageAdded: false,
                signagePanels: 0,
                fenceAreas: []
            },
            epaQuestions: {
                tankLidExposed: -1,
                tankLidLocationKnown: -1,
                tankLidLocation: "",
                truckLargeAccess: -1,
                truckSmallAccess: -1,
                truckAccessToTank: -1,  //within 25m
                steepDriveway: -1,
                hoseMetres: 0,
                sealedDriveway: -1,
                lastService: "",
                waterBoard: "",
                waterAccessOnSite: -1,
                emergencySameDayJob: -1,
                weekendJob: -1,
                siteVisit: -1,
                confinedSpace: -1,
                materialSafetyDataSheet: -1,
                sampleTestingRequired: -1
            },
            total: 0,
            totalFormatted: "$0.00",
            invoiceTotal: 0
        };

        //cater for blank dates - api needs null
        jobDetails.eventStartDate = jobDetails.eventStartDate === "" ? null : jobDetails.eventStartDate;
        jobDetails.eventEndDate = jobDetails.eventEndDate === "" ? null : jobDetails.eventEndDate;
        jobDetails.deliveryWindowStart = jobDetails.deliveryWindowStart === "" ? null : jobDetails.deliveryWindowStart;
        jobDetails.deliveryWindowEnd = jobDetails.deliveryWindowEnd === "" ? null : jobDetails.deliveryWindowEnd;
        jobQuote.jobInvoicedDate = jobQuote.jobInvoicedDate === "" ? null : jobQuote.jobInvoicedDate;
        for (var i = 0; i < jobQuote.items.length; i++) {
            var quoteItem = jobQuote.items[i];
            quoteItem.offhireDate = quoteItem.offhireDate === "" ? null : quoteItem.offhireDate;
            quoteItem.pickupDate = quoteItem.pickupDate === "" ? null : quoteItem.pickupDate;
        }

        var saveJob = {
            jobDetails: jobDetails,
            jobQuote: jobDetails.jobType === 1 ? clearQuote : jobQuote
        };

        try {
            var jsonItem = JSON.stringify(saveJob);

            const apiUrl = "SaveNewJobDetails";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                    setSaveDisabled(false);
                } else {
                    toast.success("Job Saved");
                    var jobId = result.data.id;

                    //email job request to customer
                    var reportName = jobDetails.jobType === 2 ? "JobRequest" : jobDetails.jobType === 3 ? "JobQuoteRequest" : "";
                    if (systemCompanyId === 3 && reportName != "") {
                        //this will either send the email or just save the file/s to the job
                        sendReport(reportName, jobId);
                    } else {
                        //upload any files added to the job or go straight to the new job
                        if (jobFiles.length > 0) {
                            saveJobFiles(result.data.id);
                        } else {
                            //then need to go to job search?
                            navigate("/job/" + result.data.id + "/details")
                        }
                    }
                }
            }
        } catch (e) {
            toast.error("Error saving job!");
            setSaveDisabled(false);
        }
    }

    const sendReport = async (reportName: string, jobId: number) => {
        try {
            const apiUrl = 'Get' + reportName + 'Data?JobId=' + jobId + "&reportName=" + reportName + "&param1=0";
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    //Run Job Request / Quote Request report
                    const baseURL = window.location.origin;
                    var report = new Stimulsoft.Report.StiReport();
                    var reportFile = Stimulsoft.System.IO.File.getFile(baseURL + "/reports/" + result.data.reportFileName);
                    report.load(reportFile);
                    report.dictionary.dataStore.clear();

                    var reportData = JSON.stringify(result.data.reportData);
                    //Dataset created
                    var dataSet = new Stimulsoft.System.Data.DataSet("data");
                    dataSet.readJson(reportData)
                    report.regData(dataSet.dataSetName, "", dataSet);
                    report.reportName = result.data.reportName;
                    report.dictionary.connect(false);
                    report.dictionary.synchronize();

                    //add relationships between data
                    for (var i = 0; i < result.data.reportRelationships.length; i++) {
                        var dataRelation = new Stimulsoft.Report.Dictionary.StiDataRelation(result.data.reportRelationships[i].sourceName, result.data.reportRelationships[i].name, result.data.reportRelationships[i].alias, report.dictionary.dataSources.getByName(result.data.reportRelationships[i].parentTableName), report.dictionary.dataSources.getByName(result.data.reportRelationships[i].childTableName), [result.data.reportRelationships[i].parentFieldName], [result.data.reportRelationships[i].childFieldName]);
                        report.dictionary.relations.add(dataRelation);
                        report.dictionary.synchronize();
                    }
                    report.render();

                    //Download the report to a pdf file
                    var service = new Stimulsoft.Report.Export.StiPdfExportService();
                    var stream = new Stimulsoft.System.IO.MemoryStream();
                    var settings = new Stimulsoft.Report.Export.StiPdfExportSettings();
                    service.exportTo(report, stream, settings);
                    var bytes = stream.toArray();
                    var fileData = [new Uint8Array(bytes)];
                    var fileName = reportName.toLowerCase().includes("quote") ? "Quote Request Confirmation" : "Order Confirmation";
                    var saveFile = new File(fileData, fileName, { type: 'application/pdf' });

                    emailReport(jobId, saveFile);
                }
            }
        } catch (e) {
            toast.error("Error getting data!");
        } 
    }

    //email confirmation, T&C's to customer if email box ticked otherwise just save files 
    //upload any files added to the job
    const emailReport = async (jobId: number, fileToSend: File) => {
        try {
            //Send the report to the back-end to send the email to the customer
            const formdata = new FormData()
            formdata.append('JobId', jobId.toString());
            formdata.append('SendEmail', jobDetails.sendEmailToCustomer.toString());
            formdata.append('Report', fileToSend);
            for (var i = 0; i < jobFiles.length; i++) {
                formdata.append('Plan', jobFiles[i]);      //uploading plans at the same time so I don't need to do 2 separate calls
            }

            const apiUrl = "SendConfirmationEmail";
            var result = await PostFormDataQuery(apiUrl, formdata);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                    navigate("/job/" + jobId + "/details");   //going to the job anyway as job is saved successfully
                } else {
                    navigate("/job/" + jobId + "/details");
                }
            }
        } catch (e) {
            toast.error("Error saving job!");
        }
    }

    const saveJobFiles = async (jobId: number) => {
        try {
            const filedata = new FormData()
            filedata.append('jobId', jobId.toString());
            filedata.append('fileTypeId', "1");

            for (var i = 0; i < jobFiles.length; i++) {
                filedata.append('files', jobFiles[i]);
            }
            const apiUrl = "SaveJobFiles";
            var result = await PostFormDataQuery(apiUrl, filedata);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                    setSaveDisabled(false);
                } else {
                    toast.success("Plan/s have been saved");
                    //then need to go to job search?
                    navigate("/job/" + jobId + "/details");
                }
            }
        } catch (e) {
            toast.error("Error saving job file!");
        }
    }

    const _prev = () => {
        if (currentStep <= 1) {
            setCurrentStep(1);
        } else {
            var jobTypeId = (jobDetails.jobType * 1);
            if (currentStep === 5 && jobTypeId === 1) {
                setCurrentStep(currentStep - 2);  //skip pricelist and confirmation step as job will be quoted later
            } else {
                setCurrentStep(currentStep - 1);
            }
        }
        setSaveDisabled(false);
        setGoingNext(false);
    }

    var contents;
    if (currentStep == 1) {
        let siteContacts: SiteContacts = {
            siteContactName: jobQuote.siteContactName,
            siteContactPhone: jobQuote.siteContactPhone,
            siteContact2: jobQuote.siteContact2
        };

        contents = <NewJobDetails jobDetails={jobDetails} systemCompanyId={systemCompanyId} customers={customers} customerContacts={customerContacts} siteContacts={siteContacts} yesNo={yesNo} update={updateJobDetails} updateNumber={updateJobDetailsNumber} updateChecked={updateJobDetailsChecked} updateSiteContacts={updateSiteContacts} updateCustomer={updateCustomer} updateAddress={updateAddress} clearSiteAddress={clearSiteAddress} clearCustomerAddress={clearCustomerAddress} updateCustomerContacts={updateCustomerContacts} save={_next} />
    }
    else if (currentStep == 2) {
        let jobAddress: JobAddress = {
            siteAddress: jobDetails.siteAddress,
            siteAddressExists: jobDetails.siteAddressExists,
            manualStreetName: jobDetails.manualStreetName,
            manualSuburb: jobDetails.manualSuburb,
            manualState: jobDetails.manualState,
            manualPostCode: jobDetails.manualPostCode
        };
        contents = <NewJobMatchingJobs currentStep={currentStep} jobAddress={jobAddress} goingNext={goingNext} goPrev={_prev} goNext={_next} />
    }
    else if (currentStep == 3) {
        let siteAddressDuration = jobDetails.siteAddressDuration === 0 ? jobDetails.addressSiteAddressDuration : jobDetails.siteAddressDuration;
        contents = <NewJobPricelist currentStep={currentStep} customerId={jobDetails.customerId ?? 0} priceOnly={jobDetails.priceOnly} jobTypeId={jobDetails.jobType} isEvent={jobDetails.isEvent} isThreeDayHire={jobDetails.isThreeDayHire} useTyabbPricelist={jobDetails.useTyabbPricelist} accountTypeId={jobDetails.accountTypeId} customerQuoteNotesDunny={jobDetails.customerQuoteNotesDunny} customerInvoiceQuotedAmount={jobDetails.customerInvoiceQuotedAmount} siteAddressDuration={siteAddressDuration} itemOptions={itemOptions} extraItemOptions={extraItemOptions} frequencyOptions={frequencyOptions} gateCode={jobDetails.gateCode} powerPoleDistanceOptions={powerPoleDistanceOptions} powerPoleLocationOptions={powerPoleLocationOptions} superSafeSupplierOptions={superSafeSupplierOptions} epaExtra30MinOnSiteFeeFormatted={epaExtra30MinOnSiteFeeFormatted} emergencyFeeFormatted={emergencyFeeFormatted} weekendFeeFormatted={weekendFeeFormatted} truckHireRate={truckHireRate} allowAllocateToPastDate={allowAllocateToPastDate} lunchRoomExtraId={lunchRoomExtraId} siteOfficeExtraId={siteOfficeExtraId} truckOptions={truckOptions} truckSideOptions={truckSideOptions} yesNoOptions={yesNo} notes={jobDetails.notes} quote={jobQuote} files={jobFiles} customerChargeFuelSurcharge={jobDetails.customerChargeFuelSurcharge} customerNewFuelInvoice={jobDetails.customerNewFuelInvoice} chargeFuelSurchargeSwitchedOn={chargeFuelSurchargeSwitchedOn} goPrev={_prev} updateNotes={updateJobNotes} updateGateCode={updateGateCode} update={updateJobQuote} updateFiles={updateJobFiles} goNext={_next} />
    }
    else if (currentStep == 4) {
        var jobQuoteInvTotal = jobQuote.invoiceTotal * 1;
        var jobQuoteTotal = jobQuote.total * 1;
        let invTotal = jobQuoteInvTotal === 0 ? jobQuoteTotal : jobQuoteInvTotal;
        let contact = jobDetails.customerContactFirstName + ' ' + jobDetails.customerContactLastName + ' - ' + jobDetails.customerContactPhone + ' - ' + jobDetails.customerContactEmail;
        contents = <NewJobInvoice currentStep={currentStep} siteAddress={jobDetails.siteAddress} customerName={jobDetails.customerName} contact={contact} extraInvoiceItems={jobQuote.invoiceItems} quoteItems={jobQuote.items} invoiceTotal={invTotal} addInvoiceItem={addInvoiceItem} deleteInvoiceItem={deleteInvoiceItem} goPrev={_prev} goNext={_next} />
    }
    else if (currentStep == 5) {
        contents = <NewJobNote currentStep={currentStep} saveDisabled={saveDisabled} notes={jobDetails.notes} goPrev={_prev} update={updateJobNotes} goNext={_next}  />
    } 

    let contentsFinal = loading
        ? <p><em>Loading...</em></p>
        : contents;

    return <div>
        <h1>New {jobDetails.jobType === 3 ? "Quote" : "Job"}</h1>
        <div className={systemCompanyId === 3 && (jobDetails.jobType === 1 || jobDetails.jobType === 3) ? "newJobPricelist--prepaid" : "hidden"}>QUOTE ONLY - This job WILL NOT be scheduled until customer go-ahead received!</div>
        {contentsFinal}
    </div>;
}

export default NewJobData;